import { faStars } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { MiniStroller } from "@/mk2/apps/strollers/components/MiniStroller";
import { FEATURES_LIST } from "@/mk2/apps/strollers/features";
import {
    strollersAddReviewUrl,
    strollersDetailUrl,
    strollersListUrl,
    strollersReviewsUrl,
} from "@/mk2/apps/strollers/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { url } from "@/mk2/helpers/urls";
import {
    Features,
    ProductEntity,
    StrollerBrandEntity,
    StrollerEntity,
    StrollerPhoto,
} from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    ADD_REVIEW_FOR_STROLLER_SALE_FASTER,
    ADD_REVIEW_FOR_STROLLER_TO_BOOST_SALE_stroller_boost,
    STROLLER_ADD_REVIEW,
    STROLLER_BRAND,
    STROLLER_INFO_MORE_ABOUT_model,
    STROLLER_INFO_SHOW_REVIEWS,
} from "@/mk/autogenerated/translations/StrollerInfo.17f8e07f987bc11a391ea471404697cd";

import styles from "./StrollerInfo.mscss";

export interface OwnProps {
    className?: string;
    stroller: Pick<StrollerEntity, "name" | "slug" | "userScore" | "reviewsCount"> & {
        brand: Pick<StrollerBrandEntity, "slug" | "name">;
        features: Pick<
            Features,
            "type" | "weight_in_kg" | "unfolded_dimensions_in_cm" | "origin_countries"
        >;
        previewPhoto: StrollerPhoto;
    };
    isSeller: boolean;
    strollerReviewedBySeller: boolean;
    product: Pick<ProductEntity, "id">;
}

type Props = OwnProps;

export const StrollerInfo: React.FunctionComponent<Props> = React.memo(
    ({ className, stroller, strollerReviewedBySeller, isSeller, product }) => {
        const features = [
            FEATURES_LIST.type,
            FEATURES_LIST.weight_in_kg,
            FEATURES_LIST.unfolded_dimensions_in_cm,
            FEATURES_LIST.origin_countries,
        ].filter((f) => f.hasWorthyValue(stroller.features));

        let reviewsCTA;

        if (isSeller) {
            // predavajuci pozera na inzerat
            if (!strollerReviewedBySeller) {
                reviewsCTA = (
                    <div className={styles.StrollerInfo__addReview}>
                        <div className={styles.StrollerInfo__addReview__stars}>
                            <FontAwesomeIcon icon={faStars} />
                        </div>
                        <div className={styles.StrollerInfo__addReview__text}>
                            <Interpolate
                                parent="p"
                                i18nKey={ADD_REVIEW_FOR_STROLLER_TO_BOOST_SALE_stroller_boost}
                                stroller={<strong>{stroller.name}</strong>}
                                boost={<strong>{ADD_REVIEW_FOR_STROLLER_SALE_FASTER}</strong>}
                            />
                            <Btn
                                link={url(
                                    strollersAddReviewUrl,
                                    { slug: stroller.slug },
                                    { prf: "stroller-info" },
                                )}
                                type={BtnType.Blue}
                                label={STROLLER_ADD_REVIEW}
                                className={styles.StrollerInfo__addReview__button}
                            />
                        </div>
                    </div>
                );
            } else {
                // uz napisala hodnotenie, ktore este caka na schvalenie
                if (stroller.reviewsCount > 0) {
                    reviewsCTA = (
                        <Btn
                            type={BtnType.BlueOutline}
                            label={`${STROLLER_INFO_SHOW_REVIEWS} (${stroller.reviewsCount})`}
                            link={url(strollersReviewsUrl, { slug: stroller.slug })}
                        />
                    );
                } else {
                    reviewsCTA = null;
                }
            }
        } else {
            // kupujuci pozera na inzerat
            if (stroller.reviewsCount > 0) {
                reviewsCTA = (
                    <Btn
                        type={BtnType.BlueOutline}
                        label={`${STROLLER_INFO_SHOW_REVIEWS} (${stroller.reviewsCount})`}
                        link={url(strollersReviewsUrl, { slug: stroller.slug })}
                    />
                );
            } else {
                reviewsCTA = (
                    <Btn
                        type={BtnType.BlueOutline}
                        label={STROLLER_ADD_REVIEW}
                        link={url(strollersAddReviewUrl, { slug: stroller.slug })}
                    />
                );
            }
        }

        return (
            <Panel className={cx(styles.StrollerInfo, className)}>
                <MiniStroller stroller={stroller} className={styles.StrollerInfo__miniStroller} />
                <div className={styles.StrollerInfo__reviews}>{reviewsCTA}</div>
                <div className={styles.StrollerInfo__features}>
                    <div
                        className={cx(
                            styles.StrollerInfo__features__feature,
                            styles["StrollerInfo__features__feature--odd"],
                        )}
                    >
                        <div className={styles.FeaturesList__brand__label}>{STROLLER_BRAND}</div>
                        <Link to={url(strollersListUrl, { slug: stroller.brand.slug })}>
                            {stroller.brand.name}
                        </Link>
                    </div>
                    {features.map((feature, index) => (
                        <div
                            key={feature.featureName}
                            className={cx(
                                styles.StrollerInfo__features__feature,
                                index % 2 === 0
                                    ? styles["StrollerInfo__features__feature--even"]
                                    : styles["StrollerInfo__features__feature--odd"],
                            )}
                        >
                            <div className={styles.FeaturesList__brand__label}>
                                {feature.getLabelMessage()}
                            </div>
                            <feature.Component features={stroller.features} feature={feature} />
                        </div>
                    ))}
                </div>

                <div className={styles.StrollerInfo__more}>
                    <Btn
                        type={BtnType.BlueOutline}
                        label={interpolate(STROLLER_INFO_MORE_ABOUT_model, {
                            model: stroller.name.replace(stroller.brand.name, ""),
                        })}
                        link={url(strollersDetailUrl, { slug: stroller.slug })}
                    />
                </div>
            </Panel>
        );
    },
);

StrollerInfo.displayName = "StrollerInfo";
